import {useState} from 'react';

/**
 * Debug panel for nova_search React app.
 *
 * Enable via browser console:
 *   localStorage.setItem('nova_search_debug', '1'); location.reload();
 * Or visit any page with ?search_debug=1 in the URL.
 *
 * Disable:
 *   localStorage.removeItem('nova_search_debug'); location.reload();
 */

// ─── Shared styles ────────────────────────────────────────────────────────────

const BTN = {
  background: '#2d2d2d', color: '#ccc', border: '1px solid #444', borderRadius: '3px',
  padding: '2px 8px', cursor: 'pointer', fontSize: '11px', width: '100%',
  display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '6px', margin: '0',
};

const PRE = {
  background: '#1a1a1a', color: '#7ec8e3', margin: '0', padding: '8px',
  fontSize: '10px', overflowX: 'auto', maxHeight: '300px', overflowY: 'auto',
  border: '1px solid #333', borderTop: 'none',
};

const STATUS_COLORS = {green: '#2ecc71', yellow: '#f9ca24', red: '#e74c3c', unreachable: '#e74c3c', error: '#e74c3c'};

// ─── CopyButton ───────────────────────────────────────────────────────────────

function CopyButton({text}) {
  const [copied, setCopied] = useState(false);

  function copy(e) {
    e.stopPropagation();
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  return (
    <button onClick={copy} style={{
      background: copied ? '#1a3a1a' : '#333',
      color: copied ? '#2ecc71' : '#888',
      border: '1px solid #555',
      borderRadius: '3px',
      padding: '1px 6px',
      cursor: 'pointer',
      fontSize: '10px',
      marginLeft: 'auto',
      flexShrink: 0,
    }}>
      {copied ? '✓ copied' : 'copy'}
    </button>
  );
}

// ─── ActiveFilters ────────────────────────────────────────────────────────────

function ActiveFilters({requestUrl}) {
  if (!requestUrl) return null;
  const params = new URLSearchParams(requestUrl.split('?')[1] || '');
  const section = params.get('section');
  const type = params.get('type');
  if (!section && !type) return null;

  return (
    <div style={{
      marginBottom: '8px', padding: '4px 8px',
      background: '#0d1f0d', border: '1px solid #2d5a2d', borderRadius: '3px',
      fontSize: '11px',
    }}>
      <span style={{color: '#666'}}>Filters active: </span>
      {section && <span style={{color: '#2ecc71', marginRight: '12px'}}>section=<b>{section}</b></span>}
      {type && <span style={{color: '#2ecc71'}}>type=<b>{type}</b></span>}
    </div>
  );
}

// ─── JsonBlock (search results per type) ─────────────────────────────────────

function JsonBlock({label, data}) {
  const [open, setOpen] = useState(false);
  if (!data) return null;

  const isError = data.status !== 200;
  const total = data?.data?.paging?.total ?? '?';
  const resultCount = data?.data?.results?.length ?? 0;
  const json = JSON.stringify(data, null, 2);

  return (
    <div style={{marginBottom: '6px'}}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{...BTN, borderColor: isError ? '#6b1a1a' : '#444'}}
      >
        {open ? '▼' : '▶'} {label}
        {isError
          ? <span style={{color: '#e74c3c', marginLeft: '8px'}}>⚠ error</span>
          : <span style={{color: '#888', marginLeft: '8px'}}>{resultCount} results / {total} total</span>
        }
        <CopyButton text={json} />
      </button>
      {open && (
        <>
          {isError && (
            <div style={{
              background: '#1a0000', padding: '6px 8px', fontSize: '10px',
              border: '1px solid #6b1a1a', borderTop: 'none',
            }}>
              <div style={{color: '#e74c3c', fontWeight: 'bold', marginBottom: '2px'}}>{data.message}</div>
              {data.cause && <div style={{color: '#ff9999'}}>{data.cause}</div>}
            </div>
          )}
          <pre style={PRE}>{json}</pre>
        </>
      )}
    </div>
  );
}

// ─── AutocompleteBlock ────────────────────────────────────────────────────────

function AutocompleteBlock({autocomplete}) {
  const [open, setOpen] = useState(false);
  if (!autocomplete) return null;

  const {url, response} = autocomplete;
  const count = Array.isArray(response) ? response.length : '?';
  const json = JSON.stringify(response, null, 2);

  return (
    <div style={{marginBottom: '6px'}}>
      <button onClick={() => setOpen(o => !o)} style={BTN}>
        {open ? '▼' : '▶'} Autocomplete
        <span style={{color: '#888', marginLeft: '8px'}}>{count} suggestion{count !== 1 ? 's' : ''}</span>
        <CopyButton text={json} />
      </button>
      {open && (
        <div style={{border: '1px solid #333', borderTop: 'none', background: '#141414'}}>
          <div style={{padding: '4px 8px', borderBottom: '1px solid #222'}}>
            <a href={url} target="_blank" rel="noreferrer"
               style={{color: '#2ecc71', fontSize: '10px', wordBreak: 'break-all', textDecoration: 'underline'}}>
              {url}
            </a>
          </div>
          <pre style={PRE}>{json}</pre>
        </div>
      )}
    </div>
  );
}

// ─── EsQueriesSection ─────────────────────────────────────────────────────────

const ES_LABELS = {
  topics: 'Topics',
  news: 'News / Actueel',
  books: 'Wet- en regelgeving',
  legislative_advices: 'Wetgevingsadviezen',
};

function EsQueriesSection({queries}) {
  const [open, setOpen] = useState(false);
  if (!queries) return null;

  return (
    <div style={{marginBottom: '6px'}}>
      <button onClick={() => setOpen(o => !o)} style={BTN}>
        {open ? '▼' : '▶'} Elasticsearch queries
        <span style={{color: '#888', marginLeft: '8px'}}>4 queries</span>
      </button>
      {open && (
        <div style={{border: '1px solid #333', borderTop: 'none', padding: '6px', background: '#141414'}}>
          {Object.entries(ES_LABELS).map(([key, label]) => {
            const query = queries[key];
            if (!query) return null;
            const json = JSON.stringify(query, null, 2);
            return <EsQueryBlock key={key} label={label} json={json} />;
          })}
        </div>
      )}
    </div>
  );
}

function EsQueryBlock({label, json}) {
  const [open, setOpen] = useState(false);

  return (
    <div style={{marginBottom: '4px'}}>
      <button onClick={() => setOpen(o => !o)} style={{...BTN, fontSize: '10px', background: '#222'}}>
        {open ? '▼' : '▶'} {label}
        <CopyButton text={json} />
      </button>
      {open && <pre style={{...PRE, borderColor: '#222'}}>{json}</pre>}
    </div>
  );
}

// ─── ConnectionInfo ───────────────────────────────────────────────────────────

function ConnectionInfo() {
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  function load() {
    if (info || loading) return;
    setLoading(true);
    fetch('/api/search/debug/connection')
      .then(r => {
        if (!r.ok) throw new Error(r.status === 403 ? 'Access denied (requires admin)' : `HTTP ${r.status}`);
        return r.json();
      })
      .then(data => { setInfo(data); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }

  function toggle() {
    if (!open) load();
    setOpen(o => !o);
  }

  const status = info?.cluster_health?.status;

  return (
    <div style={{marginBottom: '6px'}}>
      <button onClick={toggle} style={BTN}>
        {open ? '▼' : '▶'} Connection info
        {status && <span style={{color: STATUS_COLORS[status] ?? '#ccc', marginLeft: '8px'}}>● {status}</span>}
        {loading && <span style={{color: '#888', marginLeft: '8px'}}>loading…</span>}
      </button>
      {open && (
        <div style={{background: '#1a1a1a', border: '1px solid #333', borderTop: 'none', padding: '8px', fontSize: '11px'}}>
          {error && <div style={{color: '#e74c3c'}}>{error}</div>}
          {info && (
            <>
              <Row label="Server" value={`${info.server.name} (${info.server.id})`} />
              <Row label="Backend" value={info.server.backend} />
              <Row label="Available" value={info.server.available ? '✓ yes' : '✗ no'}
                   color={info.server.available ? '#2ecc71' : '#e74c3c'} />
              {info.connector && <>
                <div style={{borderTop: '1px solid #333', margin: '6px 0'}} />
                <Row label="Connector" value={info.connector.type} />
                <Row label="URL"
                     value={info.connector.url + (info.connector.url_override ? ' ⚠ overridden' : '')}
                     color={info.connector.url_override ? '#f9ca24' : undefined} />
                <Row label="Auth"
                     value={info.connector.use_auth ? `basic (${info.connector.username})${info.connector.auth_override ? ' ⚠ overridden' : ''}` : 'none'}
                     color={info.connector.auth_override ? '#f9ca24' : undefined} />
                {(info.connector.prefix || info.connector.suffix) &&
                  <Row label="Index affixes" value={`prefix="${info.connector.prefix ?? ''}"  suffix="${info.connector.suffix ?? ''}"`} />
                }
              </>}
              {info.cluster_health && <>
                <div style={{borderTop: '1px solid #333', margin: '6px 0'}} />
                <Row label="Cluster name" value={info.cluster_health.cluster_name} />
                <Row label="Health" value={info.cluster_health.status}
                     color={STATUS_COLORS[info.cluster_health.status]} />
                {info.cluster_health.number_of_nodes !== undefined && <>
                  <Row label="Nodes" value={info.cluster_health.number_of_nodes} />
                  <Row label="Active shards" value={`${info.cluster_health.active_shards} (${info.cluster_health.active_primary_shards} primary)`} />
                  <Row label="Unassigned shards" value={info.cluster_health.unassigned_shards} />
                </>}
                {info.cluster_health.message && <Row label="Message" value={info.cluster_health.message} color="#e74c3c" />}
              </>}
              {info.indexes?.length > 0 && <>
                <div style={{borderTop: '1px solid #333', margin: '6px 0'}} />
                {info.indexes.map(idx => (
                  <div key={idx.id} style={{marginBottom: '4px'}}>
                    <Row label="Index" value={`${idx.name} (${idx.id})`} />
                    <Row label="Fields" value={idx.field_count} />
                    {idx.read_only && <Row label="Read only" value="yes" color="#f9ca24" />}
                  </div>
                ))}
              </>}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Row({label, value, color}) {
  return (
    <div style={{display: 'flex', gap: '8px', marginBottom: '2px'}}>
      <span style={{color: '#888', minWidth: '110px', flexShrink: 0}}>{label}:</span>
      <span style={{color: color ?? '#ccc', wordBreak: 'break-all'}}>{value ?? '—'}</span>
    </div>
  );
}

// ─── Main panel ───────────────────────────────────────────────────────────────

export default function DebugPanel({requestUrl, response, durationMs, esQueries, autocomplete}) {
  const [minimized, setMinimized] = useState(false);

  const errorCount = response
    ? ['topics', 'news', 'books', 'legislative_advices']
        .filter(k => response[k] && response[k].status !== 200).length
    : 0;

  function disableDebug() {
    localStorage.removeItem('nova_search_debug');
    location.reload();
  }

  return (
    <div style={{
      position: 'fixed', bottom: '0', right: '0',
      width: minimized ? 'auto' : '500px',
      background: '#111', color: '#eee', fontFamily: 'monospace', fontSize: '11px',
      zIndex: 99999, border: '1px solid #555', borderBottom: 'none', borderRight: 'none',
      borderRadius: '4px 0 0 0', boxShadow: '-2px -2px 8px rgba(0,0,0,0.5)',
    }}>
      {/* Header */}
      <div
        onClick={() => setMinimized(m => !m)}
        style={{
          background: errorCount > 0 ? '#7b1a1a' : '#c0392b',
          color: '#fff', padding: '4px 8px', display: 'flex',
          justifyContent: 'space-between', alignItems: 'center',
          cursor: 'pointer', userSelect: 'none', fontSize: '11px', fontWeight: 'bold',
          borderRadius: minimized ? '4px 0 0 0' : undefined,
        }}
      >
        <span>Nova Search Debug {minimized ? '(minimized)' : ''}</span>
        <span style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
          {!minimized && errorCount > 0 && (
            <span style={{color: '#ff9999', fontWeight: 'normal'}}>⚠ {errorCount} error{errorCount > 1 ? 's' : ''}</span>
          )}
          {!minimized && durationMs !== null && (
            <span style={{color: '#f9ca24', fontWeight: 'normal'}}>{durationMs}ms</span>
          )}
          <span
            title="Disable debug mode"
            onClick={(e) => { e.stopPropagation(); disableDebug(); }}
            style={{cursor: 'pointer', opacity: 0.7}}
          >✕ disable</span>
        </span>
      </div>

      {/* Body */}
      {!minimized && (
        <div style={{padding: '8px'}}>

          {/* Request URL */}
          <div style={{marginBottom: '8px', display: 'flex', alignItems: 'flex-start', gap: '6px'}}>
            <span style={{color: '#aaa', flexShrink: 0}}>Request:</span>
            {requestUrl
              ? <>
                  <a href={requestUrl} target="_blank" rel="noreferrer"
                     style={{color: '#2ecc71', wordBreak: 'break-all', textDecoration: 'underline', flex: 1}}>
                    {requestUrl}
                  </a>
                  <CopyButton text={requestUrl} />
                </>
              : <span style={{color: '#888'}}>— no query yet —</span>
            }
          </div>

          {/* Active filters */}
          <ActiveFilters requestUrl={requestUrl} />

          {/* Connection info */}
          <ConnectionInfo />

          {/* Autocomplete */}
          <AutocompleteBlock autocomplete={autocomplete} />

          {/* ES queries */}
          <EsQueriesSection queries={esQueries} />

          {/* Result blocks */}
          {response && (
            <>
              <JsonBlock label="Topics" data={response.topics} />
              <JsonBlock label="News / Actueel" data={response.news} />
              <JsonBlock label="Wet- en regelgeving (books)" data={response.books} />
              <JsonBlock label="Wetgevingsadviezen" data={response.legislative_advices} />
            </>
          )}
        </div>
      )}
    </div>
  );
}
